import { Agent } from "./agent";
import { CBHDocument } from "./document";
import { LegacyGeoLocationCoordinatesAndType } from "./util";
import {
  RateNegotiationConfigEnum,
  RateNegotiationSchema,
} from "../../../../app/rateNegotiation/interfaces";
import { FacilityNote } from "../../../../appV2/Facilities/types";
import { ShiftPeriod, ShiftSpecificationCategory } from "../../../../appV2/Shifts/Shift/types";

export enum SignatoryStatus {
  REQUESTED = "REQUESTED",
  REJECTED = "REJECTED",
  SIGNED = "SIGNED",
}

export enum ShiftInOut {
  CLOCK = "clockInOut",
  LUNCH = "lunchInOut",
}

export enum GeofenceStatusType {
  entry = "entry",
  exit = "exit",
  startBreak = "start-break",
  endBreak = "end-break",
}

export interface VerificationPreferenceDetailsProps {
  /**
   * When you are fetching verification preference for a single facility, use this.
   */
  facilityId?: string;
  /**
   * When a list of facilities are rendered, we should pass this property to avoid
   * making different requests for each resource.
   */
  verificationPreference?: VerificationPreferences;
  // Used in unverifiedShiftListCard.tsx for extra condition
  timeSheetExists?: any;
}

export enum FacilityTypes {
  BEHAVIOURAL_HEALTH_CLINIC = "Behavioural Health Clinic",
  CRISIS_CENTER = "Crisis Center",
  DENTAL_CLINIC = "Dental Clinic",
  EXPLORATORY_REMOTE = "Exploratory Remote",
  HOME_HEALTHCARE = "Home Healthcare",
  HOSPICE = "Hospice",
  HOSPITAL = "Hospital",
  HOTEL = "Hotel",
  LONG_TERM_ACUTE_CENTER = "Long Term Acute Center",
  LONG_TERM_CARE = "Long Term Care",
  MEDICAL_LAB = "Medical Lab",
  NURSING_HOME = "Nursing Home",
  PHARMACY = "Pharmacy",
  PSYCH_HOSPITAL = "Psych Hospital",
  SUBSTANCE_ABUSE_DETOX_CENTER = "Substance Abuse Detox Center",
  SURGERY_CENTER = "Surgery Center",
  LONG_TERM_ACUTE_CARE_HOSPITAL = "Long-term Acute Care Hospital",
}

export interface Facility {
  /**
   * @deprecated You are probably looking for `userId` and not this.
   * This is just a MongoDB's auto-generated internal ID which we don't regard as the facility ID in our system
   */
  _id?: string;
  userId: string;
  facilityId?: string;
  name?: string;
  phone?: string;
  fullAddress?: {
    state?: string;
    region?: string;
    formatted?: string;
    metropolitanStatisticalArea?: string;
  };
  status?: string;
  tmz?: string;
  distance?: number;
  isApproxDistance?: boolean;
  description?: string;
  profileDescription?: string;
  authorizedSignatories?: AuthorizedSignatory[];
  geoLocation?: LegacyGeoLocationCoordinatesAndType;
  instantBook?: boolean | string;
  requiredDocuments?: CBHDocument[];
  rates?: {
    CNA?: number;
    LVN?: number;
    RN?: number;
    NP?: number;
    PT?: number;
    PTA?: number;
    HHA?: number;
  };
  checkInInstructions?: string;
  isInstantPayEnabled?: boolean;
  agentHasAllDocuments?: boolean;
  verification?: VerificationPreferences;
  verificationPreference?: VerificationPreferences;
  facilityNotes?: FacilityNote[];
  locationAwareness?: string;
  profileDecriptionlastManualUpdate?: Date;
  type?: FacilityTypes;
  shiftsCount?: number;
  filteredShiftsCount?: number;
  shifts?: Shift[];
  rating?: {
    count: number;
    value: number;
  };
  requireTimecardPhoto?: boolean;
  is100InstantPayEnabled?: boolean;
  isTestAccount?: boolean;
  nfcHcpToggleEnabled?: boolean;
  nfcTag?: boolean;
  priorityAccessShiftsCount?: number;
  requiresLunchBreak?: boolean;
  lateCancellation?: {
    period: number;
    feeHours: number;
  };
  sentHomeChargeHours?: number;
  rateNegotiation?: RateNegotiationConfigEnum;
  featureSettings?: {
    radarTimekeepingValidations: boolean;
    displayTimeSheetSummary: boolean;
    digitalSignature: boolean;
    californiaTimesheet: boolean;
    uploadTimesheetNonIP: boolean;
  };
  quizWorkersOnRules?: boolean;
  maxAllowedWorkConsecutiveHours?: number;
  maxAllowedWorkHoursPerWeek?: number;
}

export type LegacyFacility = Facility;

export enum VerificationMethod {
  TIMESHEET = "Timesheet Only",
  OUTBOUND = "Outbound Verification",
  SHEET = "External Google Sheet",
  OTHER = "Other",
}
export type ContactMethod = "PHONE" | "EMAIL" | "TEXT";
export type Day =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";
export interface ContactAvailability {
  day: Day;
}
export interface VerificationContact {
  archived: boolean;
  contactMethod: {
    name: ContactMethod;
  };
  availability: ContactAvailability[];
}
export interface VerificationPreferences {
  mongodbId: string;
  name: string;
  maxOutreachAttemptsPerDay: number;
  usesTimesheets: boolean;
  verificationMethod: {
    name: VerificationMethod;
  };
  contacts: VerificationContact[];
  outboundContactList?: VerificationContact[];
  preferenceType?: string;
  verificationAttempt?: number;
  requiresLunchBreak?: boolean;
}

export interface AuthorizedSignatory {
  _id?: string;
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
}

interface RequestedSignatory extends AuthorizedSignatory {
  status: SignatoryStatus;
}

export interface TimeRange {
  startCard?: string;
  endCard?: string;
  start?: string;
  end?: string;
  isSkipped?: boolean;
  startImage?: string;
  endImage?: string;
}

export interface DisputedTimeRange {
  stamp: Date;
  stage: string;
}

export interface TimecardFiles {
  _id: string;
  url: string;
  signedType: string;
  timesheet: string;
  fileStorageFileKey: string;
  filename: string;
  fileStorageRootFolder: string;
}

export interface Timecard {
  createdAt?: Date;
  files?: TimecardFiles[];
  status?: number;
}

export interface TimecardV2 extends Timecard {
  success: boolean;
  error: string | null;
  response: object;
}

export enum ExtraWorkedTimeType {
  BREAK = "BREAK",
}

export type ExtraWorkedTimeAuthorization = {
  reason: string;
};

export type ExtraWorkedTimeParameters = {
  type: ExtraWorkedTimeType;
  authorization: ExtraWorkedTimeAuthorization;
};

export interface SurgerySpeciality {
  speciality: string;
  hasTrayAssemblyExperience: boolean;
  hasSedationExperience: boolean;
}

/**
 * Most of these enums were copied from backend-main/src/constants or similar project files
 * We should centralize them here in the future
 */

export enum InstantPayPaymentTypes {
  INITIAL = "INITIAL",
  REMAINING = "REMAINING",
  BREAK_EXTRA_WORKED_TIME = "BREAK_EXTRA_WORKED_TIME",
}

export enum InstantPayPaymentVia {
  STRIPE = "STRIPE",
  DAILY_PAY = "DAILY_PAY",
}

export enum InstantPayTimeLogStates {
  ASSIGNED = "ASSIGNED",
  ASSIGNED_FAILED = "ASSIGNED_FAILED",
  CLOCK_IN = "CLOCK_IN",
  CLOCK_OUT = "CLOCK_OUT",
  LUNCH_IN = "LUNCH_IN",
  LUNCH_OUT = "LUNCH_OUT",
  ADMIN_CLOCK = "ADMIN_CLOCK",
  ADMIN_LUNCH = "ADMIN_LUNCH",
  PARTIALY_PAID = "PARTIALY_PAID",
  FULL_PAID = "FULL_PAID",
  CANCELED = "CANCELLED",
  VERIFIED = "VERIFIED",
  TIME_ADJUSTED = "TIME_ADJUSTED",
  SKIP_LUNCH = "SKIP_LUNCH",
  SHIFT_NOTE_UPDATE = "SHIFT_NOTE_UPDATE",
  SHIFT_TIME_CHANGE = "SHIFT_TIME_CHANGE",
  POST_VERIFY_CLOCK_IN = "POST_VERIFY_CLOCK_IN",
  POST_VERIFY_CLOCK_OUT = "POST_VERIFY_CLOCK_OUT",
  POST_VERIFY_LUNCH_IN = "POST_VERIFY_LUNCH_IN",
  POST_VERIFY_LUNCH_OUT = "POST_VERIFY_LUNCH_OUT",
  AUTO_VERIFY = "AUTO_VERIFY",
  BREAK_EXTRA_WORKED_TIME_PAID = "BREAK_EXTRA_WORKED_TIME_PAID",
}

export interface InstantPayShiftPayment {
  amount: number;
  paidVia: InstantPayPaymentVia;
  type: InstantPayPaymentTypes;
}

export interface ShiftGeofence {
  entry: Date;
  exit?: Date;
  startBreak?: Date;
  endBreak?: Date;
  status: GeofenceStatusType;
}

export interface InstantPayShiftDetails {
  shiftId: string;
  hcpId: string;
  start: string;
  end: string;
  verified: boolean;
  deleted: boolean;
  status?: InstantPayTimeLogStates;
  action: InstantPayTimeLogStates;
  payments: InstantPayShiftPayment[];
  charge: number;
  time: number;
  pay: number;
  totalAmount: number;
  paidAmount: number;
  /**
   * `remaingAmount` is a typo from the API.
   */
  remaingAmount: number;
  hcpClockIn?: string;
  hcpLunchOut?: string;
  hcpLunchIn?: string;
  hcpClockOut?: string;
  adminClockIn?: string;
  adminLunchIn?: string;
  adminLunchOut?: string;
  adminClockOut?: string;
  completedDate?: string;
  postVerifyClockIn?: string;
  postVerifyClockOut?: string;
  postVerifyLunchIn?: string;
  postVerifyLunchOut?: string;
  is100InstantPayEnabled?: boolean | undefined;
  nfcTag?: boolean;
  submitClockIn?: string;
  submitLunchIn?: string;
  submitLunchOut?: string;
  submitClockOut?: string;
}

export interface Shift {
  _id?: string;
  name?: ShiftPeriod;
  verified?: boolean;
  signed?: boolean;
  agentReq?: string;
  confirmed?: boolean;
  filtered?: boolean;
  start?: string;
  end?: string;
  time?: number;
  description?: string;
  surgerySpeciality?: SurgerySpeciality;
  charge?: number;
  pay?: number;
  distance?: number;
  isApproxDistance?: boolean;
  finalPay?: number;
  offer?: {
    id: string;
    pay: {
      value: number;
      currency: string;
    };
    expiresAt?: Date;
  };
  facility?: Facility;
  facilityId?: string;
  agentId?: string;
  agent?: Agent;
  requestedSignatories?: RequestedSignatory[];
  interested?: string[];
  signatory?: AuthorizedSignatory;
  unit?: string;
  clockInOut?: TimeRange;
  lunchInOut?: TimeRange;
  disputedTimeout?: DisputedTimeRange;
  agentSignature?: string;
  timecard?: Timecard;
  geofence?: ShiftGeofence;
  isHolidayShift?: boolean;
  holidayName?: string;
  rating?: {
    FACILITY?: number;
    AGENT?: number;
  };
  cancellationReason?: {
    type: string;
  };
  status?: number;
  isHCFInstantPayProhibited?: boolean;
  instantPayDetails?: InstantPayShiftDetails;
  agentHasAllDocuments?: boolean;
  isInstantPayEnabled?: boolean;
  shiftId?: string;
  isInstantBook?: boolean;
  isInstantPay?: boolean;
  isChangedToNonInstantPay?: boolean;
  instruction?: string;
  isHCFConfirmed?: boolean;
  /**
   * Only present in response for lastMinuteShifts.
   */
  isInstantForAgent?: boolean;
  isInstantPayWorkedBefore?: boolean;
  groupId?: string;
  urgency?: number;
  hidden?: number;
  bookable?: boolean;
  timecardNotAvailable?: {
    reason: string;
    setBy: string;
    updatedAt: string;
  };
  isStripePayment?: boolean;
  reminderNotify?: [];
  submitClockInOut?: TimeRange;
  submitLunchInOut?: TimeRange;
  autoClockedOut?: boolean;
  deleted?: boolean;
  isBillable?: boolean;
  nfcTag?: boolean;
  originalAmount?: number;
  priorityTill?: string;
  nfcTagRequests?: tagRequest[];
  extendBookabilityTo?: Date;
  urgentlyBooked?: boolean;
  rateNegotiation?: RateNegotiationSchema;
  createdAt?: string;
  isExternal?: boolean;
  hasPreviousUnassign?: boolean;
  shiftTimeNegotiationActive?: boolean;
  /**
   * payBreakDown and chargeBreakDown are deprecated
   * See: https://clipboardhealth.atlassian.net/browse/CH-22416
   * Use `isChargeUpdatedManually` instead
   */
  isChargeUpdatedManually?: boolean;
  hasSentHomeRequest?: boolean;
  /**
   * Whether this is a hot shift (subsidized because it's in a GTM market)
   */
  shiftBlockId?: string;
  attendance?: {
    arrivedFacilityGeofenceAt?: Date;
    leftFacilityGeofence: Boolean;
    leftFacilityGeofenceAt?: Date;
    isWorkerFarFromFacility: Boolean;
  };
  shiftSpecifications?: {
    category: ShiftSpecificationCategory;
    requirement: string;
  }[];
}
export type LegacyShift = Shift;

export interface tagRequest {
  tagStatus: string;
  tagLocation?: string;
}

export interface Shift_NEW {
  response: [] | object | null;
  error: string | null;
  success: boolean;
  instantPayDetails?: { [key: string]: any };
  agentHasAllDocuments?: boolean;
}

export interface UnverifiedShift extends Shift {
  businessrules?: {
    key: string;
    value: string;
    description: string;
  };
}

export enum SHIFT_MARKED_NON_IP_REASONS {
  NO_LOCATION = "HCP failed to confirm location",
  MOBILE_FORGOT_TO_CLOCK_IN = "Mobile: HCP failed to clock in",
  MOBILE_HCP_IS_OUTSIDE_GEOFENCE = "Mobile: HCP is outside geofence and failed to clock in",
  API_CALL = "/markNonInstant API call",
  CAUGHT_ON_AUTO_CLOCK_OUT = "HCP forgot to clock in, caught on auto clock out",
  REACHED_VERIFICATION_WITHOUT_PAYMENT = "SUSPICIOUS, Reached the verification step without initial payment",
}

export enum ShiftStages {
  CLOCK_IN = "CLOCK_IN",
  LUNCH_IN = "LUNCH_IN",
  LUNCH_OUT = "LUNCH_OUT",
  CLOCK_OUT = "CLOCK_OUT",
  SHIFT_TIME_DONE = "SHIFT_TIME_DONE",
  SKIP_LUNCH = "SKIP_LUNCH",
  UPLOAD_TIME_CARD = "UPLOAD_TIME_CARD",
  GET_FACILITY_SIGNATURE = "GET_FACILITY_SIGNATURE",
  GET_SIGNATURE = "GET_SIGNATURE",
}

export enum DeviceNFCCapabilityForShift {
  NFC_ENABLED = "NFC_ENABLED",
  NO_NFC = "NO_NFC",
  NFC_DISABLED = "NFC_DISABLED",
}

export enum NFCReadStatus {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
  INVALID_HASH = "INVALID_HASH",
  NO_NFC_HASH_FOR_FACILITY = "NO_NFC_HASH_FOR_FACILITY",
  NFC_HASH_NOT_BELONG_TO_FACILITY = "NFC_HASH_NOT_BELONG_TO_FACILITY",
  INACTIVE_NFC_HASH = "INACTIVE_NFC_HASH",
  READ_TIMEOUT = "READ_TIMEOUT",
}

export enum NFCShiftConversionReason {
  SKIP_NFC_VALIDATION = "SKIP_NFC_VALIDATION",
  NO_NFC_DEVICE = "NO_NFC_DEVICE",
  NFC_TAG_NOT_WORKING = "NFC_TAG_NOT_WORKING",
}
